<template>
  <v-row>
    <v-col cols="12" sm="6">
      <v-select
          filled
          :prepend-icon="$vuetify.breakpoint.smAndUp ? 'mdi-table-headers-eye' : ''"
          v-model="fields.type"
          :items="types"
          :label="$t('label-export-type')"
          hide-details
      >
      </v-select>
    </v-col>
    <v-col cols="12" sm="6" v-if="fields.type === 'raw'">
      <v-select
          filled
          :prepend-icon="$vuetify.breakpoint.smAndUp ? 'mdi-file-outline' : ''"
          v-model="fields.options.raw.format"
          :items="formats"
          :label="$t('label-export-format')"
          hide-details>
      </v-select>
    </v-col>
    <v-col cols="12" sm="6" v-else>
      <export-options-select
          :value.sync="fields.options.report"
          :filled="true"
          :label="$t('label-export-options')">
      </export-options-select>
    </v-col>
    <v-col cols="12">
      <relation-select
          :filled="true"
          :label="$t('label-export-targets')"
          :placeholder="$t('placeholder-export-targets')"
          :entities="['monitors', 'websites', 'tags']"
          :value.sync="fields.targets"
          :closeOnContentClick="false">
      </relation-select>
    </v-col>
  </v-row>
</template>

<script>
import RelationSelect from '@/views/Common/Components/Selects/RelationSelect'
import ExportOptionsSelect from '@/views/Common/Components/Selects/ExportOptionsSelect'

export default {
  name: 'ExportForm',
  components: { ExportOptionsSelect, RelationSelect },
  props: ['ids', 'value', 'onlyReports'],
  data () {
    const targets = this.ids ? this.ids.map(id => 'monitors:' + id) : []

    return {
      fields: {
        type: this.onlyReports ? 'daily' : 'raw',
        options: {
          raw: {
            format: 'csv'
          },
          report: {}
        },
        targets,
        ...this.value,
      },
      formats: [
        {
          text: this.$t('format-csv'),
          value: 'csv'
        },
        {
          text: this.$t('format-xlsx'),
          value: 'xlsx'
        },
        {
          text: this.$t('format-pdf'),
          value: 'pdf'
        },
        {
          text: this.$t('format-tsv'),
          value: 'tsv'
        },
        {
          text: this.$t('format-xls'),
          value: 'xls'
        },
        {
          text: this.$t('format-ods'),
          value: 'ods'
        },
        {
          text: this.$t('format-html'),
          value: 'html'
        },
      ],
    }
  },
  computed: {
    types () {
      if(this.onlyReports) {
        return [
          {
            text: this.$t('type-report-daily'),
            value: 'daily'
          },
          {
            text: this.$t('type-report-weekly'),
            value: 'weekly'
          },
          {
            text: this.$t('type-report-monthly'),
            value: 'monthly'
          },
        ];
      } else {
        return [
          {
            text: this.$t('type-export-raw'),
            value: 'raw'
          },
          {
            header: this.$t('type-report-header'),
          },
          {
            text: this.$t('type-report-daily'),
            value: 'daily'
          },
          {
            text: this.$t('type-report-weekly'),
            value: 'weekly'
          },
          {
            text: this.$t('type-report-monthly'),
            value: 'monthly'
          },
        ];
      }
    }
  },
  watch: {
    fields: {
      handler: function (val) {
        this.$emit('update:value', val)
      },
      deep: true
    }
  },
  mounted () {
    this.$emit('update:value', this.fields)
  }
}
</script>

<i18n>
{
	"en": {
		"format-csv": "CSV (comma)",
		"format-html": "HTML (Web)",
		"format-ods": "ODS (OpenOffice)",
		"format-pdf": "PDF (Adobe PDF)",
		"format-tsv": "TSV (comma)",
		"format-xls": "XLS (Excel)",
		"format-xlsx": "XLSX (Excel 2007+)",
		"label-export-format": "Export format",
		"label-export-options": "Customization options",
		"label-export-targets": "Data to export",
		"label-export-type": "Export Type",
		"placeholder-export-targets": "Leave blank to export all",
		"type-export-raw": "Raw Data",
		"type-report-daily": "Daily position report",
		"type-report-header": "Reports",
		"type-report-monthly": "Monthly position report",
		"type-report-weekly": "Weekly position report"
	},
	"es": {
		"format-csv": "CSV (coma)",
		"format-html": "HTML (Web)",
		"format-ods": "ODS (OpenOffice)",
		"format-pdf": "PDF (PDF de Adobe)",
		"format-tsv": "TSV (coma)",
		"format-xls": "XLS (Excel)",
		"format-xlsx": "XLSX (Excel 2007+)",
		"label-export-format": "Formato de exportación",
		"label-export-options": "Opciones de personalización",
		"label-export-targets": "Datos para exportar",
		"label-export-type": "Tipo de exportación",
		"placeholder-export-targets": "Dejar en blanco para exportar todo",
		"type-export-raw": "Datos brutos",
		"type-report-daily": "Informe diario de posición",
		"type-report-header": "Informes",
		"type-report-monthly": "Informe de posición mensual",
		"type-report-weekly": "Informe de posición semanal"
	},
	"fr": {
		"format-csv": "CSV (virgule)",
		"format-html": "HTML (Web)",
		"format-ods": "ODS (Open Office)",
		"format-pdf": "PDF (Adobe PDF)",
		"format-tsv": "TSV (virgule)",
		"format-xls": "XLS (Excel)",
		"format-xlsx": "XLSX (Excel 2007+)",
		"label-export-format": "Format d'export",
		"label-export-options": "Options de personnalisation",
		"label-export-targets": "Données à exporter",
		"label-export-type": "Type d'export",
		"placeholder-export-targets": "Laissez vide pour tout exporter",
		"type-export-raw": "Données brutes",
		"type-report-daily": "Rapport quotidien de positions",
		"type-report-header": "Rapports",
		"type-report-monthly": "Rapport mensuel de positions",
		"type-report-weekly": "Rapport hebdomadaire de positions"
	},
	"it": {
		"format-csv": "CSV (virgola)",
		"format-html": "HTML (Web)",
		"format-ods": "ODS (OpenOffice)",
		"format-pdf": "PDF (Adobe PDF)",
		"format-tsv": "TSV (virgola)",
		"format-xls": "XLS (Excel)",
		"format-xlsx": "XLSX (Excel 2007+)",
		"label-export-format": "Formato di esportazione",
		"label-export-options": "Opzioni di personalizzazione",
		"label-export-targets": "Dati da esportare",
		"label-export-type": "Tipo di esportazione",
		"placeholder-export-targets": "Lascia vuoto per esportare tutto",
		"type-export-raw": "Dati grezzi",
		"type-report-daily": "Rapporto giornaliero di posizioni",
		"type-report-header": "Rapporti",
		"type-report-monthly": "Rapporto mensile di posizioni",
		"type-report-weekly": "Rapporto settimanale di posizioni"
	},
	"pt": {
		"format-csv": "CSV (vírgula)",
		"format-html": "HTML (Web)",
		"format-ods": "ODS (OpenOffice)",
		"format-pdf": "PDF (Adobe PDF)",
		"format-tsv": "TSV (vírgula)",
		"format-xls": "XLS (Excel)",
		"format-xlsx": "XLSX (Excel 2007+)",
		"label-export-format": "Formato de exportação",
		"label-export-options": "Opções de personalização",
		"label-export-targets": "Dados a exportar",
		"label-export-type": "Tipo de exportação",
		"placeholder-export-targets": "Deixe em branco para exportar tudo",
		"type-export-raw": "Dados brutos",
		"type-report-daily": "Relatório diário de posições",
		"type-report-header": "Relatórios",
		"type-report-monthly": "Relatório mensal de posições",
		"type-report-weekly": "Relatório semanal de posições"
	},
	"pl": {
		"format-csv": "CSV (przecinek)",
		"format-html": "HTML (Web)",
		"format-ods": "ODS (OpenOffice)",
		"format-pdf": "PDF (Adobe PDF)",
		"format-tsv": "TSV (tabulator)",
		"format-xls": "XLS (Excel)",
		"format-xlsx": "XLSX (Excel 2007+)",
		"label-export-format": "Format eksportu",
		"label-export-options": "Opcje personalizacji",
		"label-export-targets": "Dane do eksportu",
		"label-export-type": "Typ eksportu",
		"placeholder-export-targets": "Pozostaw puste, aby wyeksportować wszystko",
		"type-export-raw": "Surowe dane",
		"type-report-daily": "Dzienny raport pozycji",
		"type-report-header": "Raporty",
		"type-report-monthly": "Miesięczny raport pozycji",
		"type-report-weekly": "Tygodniowy raport pozycji"
	}
}
</i18n>